import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useSendListQuery } from '@/client/api/campaigns/campaigns.query';
import {
  AdminSendRecord,
  CHANNEL_LABEL,
  formatEpoch,
  MessagingChannel,
  SendListParams,
} from '@/client/types/campaigns';
import { useListFilters } from '@/client/hooks/useListFilters';
import Pagination from '@/client/components/common/Pagination';

const channelsLabel = (channels?: MessagingChannel[] | string) => {
  if (!channels) return '—';
  if (typeof channels === 'string') return CHANNEL_LABEL[channels] || channels;
  return channels.map((c) => CHANNEL_LABEL[c] || c).join(', ');
};

const tallyLabel = (send: AdminSendRecord) => {
  const t = send.tally;
  if (!t) return '—';
  return `${t.sent ?? 0} sent / ${t.failed ?? 0} failed`;
};

export default function CampaignSendHistory({
  channel,
}: {
  channel?: MessagingChannel;
}) {
  const {
    searchTerm,
    debouncedSearchTerm,
    page,
    limit,
    handleSearch,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters({
    initialFilters: {},
    initialLimit: 25,
    // Shares a route with the other campaign tabs; namespace the URL params so
    // switching tabs doesn't leak this panel's q/page onto the next.
    namespace: 'hist',
  });

  const [selected, setSelected] = useState<AdminSendRecord | null>(null);

  const listParams: SendListParams = useMemo(() => {
    const actor = debouncedSearchTerm.trim()
      ? Number(debouncedSearchTerm.trim())
      : undefined;
    return {
      limit,
      offset: (page - 1) * limit,
      ...(channel ? { channel } : {}),
      ...(actor && Number.isFinite(actor) ? { actor_uid: actor } : {}),
    };
  }, [debouncedSearchTerm, page, limit, channel]);

  const { data, isLoading, isError } = useSendListQuery(listParams);
  const sends = data?.sends ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by actor UID…"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {isError && (
          <p className="p-8 text-center text-sm text-red-600">Failed to load send history.</p>
        )}
        {!isLoading && !isError && sends.length === 0 && (
          <p className="p-8 text-center text-sm text-gray-500">No sends yet.</p>
        )}
        {!isLoading && sends.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Msgid</th>
                  <th className="px-4 py-3 font-medium">Channels</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">Recipients</th>
                  <th className="px-4 py-3 font-medium">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sends.map((send) => (
                  <tr
                    key={send.msgid}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelected(send)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {formatEpoch(send.created_at)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-800">
                      {send.msgid?.slice(0, 12)}…
                    </td>
                    <td className="px-4 py-3">{channelsLabel(send.channels)}</td>
                    <td className="max-w-[220px] truncate px-4 py-3 text-gray-800">
                      {send.subject || '—'}
                    </td>
                    <td className="px-4 py-3">{send.recipients_total ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{tallyLabel(send)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && (
          <div className="border-t border-gray-100 px-4 py-3">
            <Pagination
              total={pagination.total ?? sends.length}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
            />
          </div>
        )}
      </div>

      {selected && (
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-900">Send detail</h3>
              <p className="font-mono text-xs text-gray-500">{selected.msgid}</p>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-sm text-gray-500 hover:text-gray-800"
            >
              Close
            </button>
          </div>
          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Created</dt>
              <dd>{formatEpoch(selected.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Actor</dt>
              <dd>{selected.actor_uid ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Channels</dt>
              <dd>{channelsLabel(selected.channels)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Reason</dt>
              <dd>{selected.reason || '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500">Subject</dt>
              <dd>{selected.subject || '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500">Body</dt>
              <dd className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-gray-800">
                {selected.body || '—'}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
